import {
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon
} from "@mui/icons-material";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useCallback, useEffect, useState } from "react";

import MetricCard from "../../../components/MetricCard";
import { logError } from "../../../utils/error";
import { TimeRange } from "../../../utils/timerange";
import { useSelectedGroups } from "../../auth/hooks/useSelectedGroups";
import { generateTrends } from "../utils/trend";
import PerformanceTable from "./PerformanceTable";
import { fetchProtocolMetrics, pickRandomUnique } from "./protocolQueries";

interface DetailedProtocolViewProps {
  timeRange: TimeRange;
  timezone: string;
  protocol: string;
  user: string;
  onCallClick: (interactionId: string, attribute?: string) => void;
}

const DetailedProtocolView = ({
  timeRange,
  timezone,
  protocol,
  user,
  onCallClick
}: DetailedProtocolViewProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<any>(null);
  const [callsMode, setCallsMode] = useState<"recent" | "random">("recent");
  const [rawData, setRawData] = useState<any[]>([]);
  const { permittedEmails } = useSelectedGroups();

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchProtocolMetrics({ timeRange, timezone, protocol, user, permittedEmails });
        setMetrics(data);
        setRawData(data.enrichedScorecards);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "An error occurred";
        logError("[DetailedProtocolView] Error loading data", errorMessage);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [timeRange, timezone, protocol, user, permittedEmails]);

  const handleCallsModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: "recent" | "random" | null
  ) => {
    if (newMode !== null) {
      setCallsMode(newMode);
    }
  };

  const refreshRandomCalls = useCallback(() => {
    if (!rawData.length) {
      return;
    }

    // Generate a new random selection of calls from the full dataset
    const newRandomCalls = pickRandomUnique(rawData, 20);

    // Update just the random calls portion of the metrics
    setMetrics((prev: any) => ({
      ...prev,
      randomCalls: {
        calls: newRandomCalls,
        trends: generateTrends(newRandomCalls)
      }
    }));
  }, [rawData]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!metrics) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="info">No data available for the selected protocol and time range.</Alert>
      </Box>
    );
  }

  return (
    <>
      {/* Protocol Header */}
      <Box sx={{ mt: 2, mb: 3 }}>
        <Typography variant="h5">{protocol}</Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {user !== "all" ? `Performance for ${user}` : "Overall performance"}
        </Typography>
      </Box>

      {/* Metric Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <MetricCard
            icon={<AssessmentIcon />}
            title="Total Calls"
            value={metrics.totalCalls}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <MetricCard
            icon={<AssessmentIcon />}
            title="Average Score"
            value={metrics.averageScore.toFixed(2)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <MetricCard
            icon={<TrendingUpIcon />}
            title="Max Score"
            value={metrics.maxScore.toFixed(2)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <MetricCard
            icon={<TrendingDownIcon />}
            title="Min Score"
            value={metrics.minScore.toFixed(2)}
          />
        </Grid>
      </Grid>

      {/* Top and Bottom Calls Tables */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <PerformanceTable
              title="Top 5 Calls"
              subtitle="Calls with the highest scores"
              data={metrics.topCalls}
              onCallClick={onCallClick}
            />
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <PerformanceTable
              title="Bottom 5 Calls"
              subtitle="Calls with the lowest scores"
              data={metrics.bottomCalls}
              onCallClick={onCallClick}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Recent/Random Calls Table */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6">Performance of 20 Calls</Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ToggleButtonGroup
              value={callsMode}
              exclusive
              onChange={handleCallsModeChange}
              size="small"
            >
              <ToggleButton value="recent">
                LAST 20
              </ToggleButton>
              <ToggleButton value="random">
                RANDOM 20
              </ToggleButton>
            </ToggleButtonGroup>
            {callsMode === "random" && (
              <IconButton
                onClick={refreshRandomCalls}
                size="small"
                sx={{ ml: 1 }}
                aria-label="Refresh random calls"
              >
                <RefreshIcon />
              </IconButton>
            )}
          </Box>
        </Box>
        <PerformanceTable
          data={callsMode === "recent" ? metrics.recentCalls : metrics.randomCalls}
          onCallClick={onCallClick}
        />
      </Paper>
    </>
  );
};

export default DetailedProtocolView;
